// Per-service order form schemas — different fields for each service.
// Each field: { key, label, type, required, placeholder, helper, options?, full?, max? }
// Types: text | email | url | tel | textarea | select | number

const COMMON_NOTES = {
  key: "notes",
  label: "Goals & additional notes",
  type: "textarea",
  placeholder: "What outcome are we shooting for?",
  full: true,
};

const COUNTRY_OPTIONS = ["United States", "United Kingdom", "Canada", "Australia", "Other"];

const DR_OPTIONS = ["DR 10-30", "DR 30-40", "DR 40-50", "DR 50-60", "DR 60-70", "DR 70+"];

export const SERVICE_FORMS = {
  "press-release-distribution": {
    title: "Press Release Distribution — order brief",
    fields: [
      { key: "headline", label: "Press release headline", type: "text", required: true, placeholder: "Acme Launches AI-Powered Platform" },
      { key: "company_name", label: "Company name", type: "text", required: true, placeholder: "Acme Inc." },
      { key: "target_url", label: "Target URL (landing page)", type: "url", required: true, placeholder: "https://example.com/launch" },
      { key: "industry", label: "Industry / niche", type: "text", required: true, placeholder: "SaaS, fintech, e-commerce…" },
      { key: "keywords", label: "Keywords (max 5)", type: "text", required: true, placeholder: "comma-separated" },
      { key: "press_contact_email", label: "Press contact email", type: "email", required: true, placeholder: "press@example.com" },
      { key: "press_contact_phone", label: "Press contact phone", type: "tel", placeholder: "+1 (555) 555-5555" },
      { key: "country", label: "Distribution country", type: "select", options: COUNTRY_OPTIONS, required: true },
      { key: "release_body", label: "Press release content (or paste raw notes — we'll polish it)", type: "textarea", required: true, full: true, placeholder: "Lead with a strong angle in 1–2 sentences..." },
      COMMON_NOTES,
    ],
  },

  "content-syndication": {
    title: "Content Syndication — order brief",
    fields: [
      { key: "target_url", label: "Target URL", type: "url", required: true, placeholder: "https://example.com/article" },
      { key: "anchor_text", label: "Preferred anchor text", type: "text", required: true, placeholder: "e.g. AI marketing tools" },
      { key: "dr_range", label: "DR range preference", type: "select", options: DR_OPTIONS, required: true },
      { key: "industry", label: "Industry / niche", type: "text", required: true },
      { key: "keywords", label: "Topic keywords (max 5)", type: "text", required: true, placeholder: "comma-separated" },
      { key: "competitor_examples", label: "Sites you'd like to be syndicated alongside (optional)", type: "textarea", placeholder: "one per line" },
      COMMON_NOTES,
    ],
  },

  "guest-posting": {
    title: "Guest Posting — order brief",
    fields: [
      { key: "target_url", label: "Target URL", type: "url", required: true, placeholder: "https://example.com/landing" },
      { key: "anchor_text", label: "Primary anchor text", type: "text", required: true, placeholder: "e.g. lead generation" },
      { key: "secondary_anchor", label: "Secondary/branded anchor (optional)", type: "text" },
      { key: "dr_range", label: "DR tier", type: "select", options: DR_OPTIONS, required: true },
      { key: "industry", label: "Industry / niche", type: "text", required: true, placeholder: "SaaS, finance, health…" },
      { key: "keywords", label: "Topic keywords (max 5)", type: "text", required: true, placeholder: "comma-separated" },
      { key: "topic_ideas", label: "Topic ideas (optional)", type: "textarea", placeholder: "one per line — we'll write the article" },
      { key: "excluded_sites", label: "Sites to exclude (optional)", type: "textarea", placeholder: "one domain per line" },
      COMMON_NOTES,
    ],
  },

  "high-end-backlinks": {
    title: "High-End Backlinks — order brief",
    fields: [
      { key: "target_url", label: "Target URL", type: "url", required: true },
      { key: "anchor_text", label: "Anchor text", type: "text", required: true },
      { key: "industry", label: "Industry / niche", type: "text", required: true },
      { key: "ideal_publishers", label: "Ideal publishers (optional)", type: "textarea", placeholder: "TechCrunch, Forbes, etc. — one per line" },
      { key: "pitch_angle", label: "Pitch angle / story idea", type: "textarea", required: true, full: true, placeholder: "What's the angle editors will care about?" },
      { key: "spokesperson", label: "Spokesperson (for quotes)", type: "text", placeholder: "Name + title" },
      COMMON_NOTES,
    ],
  },

  "local-citations": {
    title: "Local Citation Building — order brief",
    fields: [
      { key: "country", label: "Select country", type: "select", required: true, options: ["USA", "UK", "Australia", "Canada"] },
      { key: "business_name", label: "Business name", type: "text", required: true, placeholder: "Bookinglane" },
      { key: "website_url", label: "Website URL", type: "url", required: true, placeholder: "https://www.bookinglane.com" },
      { key: "description", label: "Brief business description (max 100 words)", type: "textarea", required: true, full: true, max: 600, placeholder: "Bookinglane provides professional black car and chauffeur services in San Francisco…" },
      { key: "keywords", label: "Keywords (max 5)", type: "text", required: true, placeholder: "Corporate transportation, Airport service…" },
      { key: "founder", label: "Founder / CEO", type: "text", required: true, placeholder: "Ivan Ivanov" },
      { key: "full_address", label: "Full business address", type: "text", required: true, placeholder: "447 Sutter St, San Francisco, CA 94108" },
      { key: "phone", label: "Phone number", type: "tel", required: true, placeholder: "+1 415-384-5039" },
      { key: "email", label: "Listing email address", type: "email", required: true, placeholder: "info@example.com" },
      { key: "business_categories", label: "Primary business categories", type: "text", placeholder: "comma-separated" },
      { key: "gbp_access", label: "Google Business Profile email (for optimization)", type: "email" },
      COMMON_NOTES,
    ],
  },

  "on-page-seo": {
    title: "On-Page SEO — order brief",
    fields: [
      { key: "website_url", label: "Website URL", type: "url", required: true },
      { key: "pages_to_optimize", label: "Pages to optimize (URLs)", type: "textarea", required: true, full: true, placeholder: "one URL per line" },
      { key: "primary_keywords", label: "Primary keyword per page", type: "textarea", required: true, full: true, placeholder: "url ↦ keyword (one per line)" },
      { key: "cms", label: "CMS / Platform", type: "select", required: true, options: ["WordPress", "Webflow", "Shopify", "Custom / Other"] },
      { key: "access_provided", label: "Will you provide CMS access?", type: "select", options: ["Yes", "No — deliver as document"], required: true },
      { key: "competitor_urls", label: "Top competitors (optional)", type: "textarea", placeholder: "one per line" },
      COMMON_NOTES,
    ],
  },

  "site-audit": {
    title: "Site Audit — order brief",
    fields: [
      { key: "website_url", label: "Website URL", type: "url", required: true, placeholder: "https://example.com" },
      { key: "industry", label: "Industry / niche", type: "text", required: true },
      { key: "primary_market", label: "Primary market (country)", type: "select", options: COUNTRY_OPTIONS, required: true },
      { key: "focus_areas", label: "Focus areas", type: "select", options: ["Full audit", "Technical SEO", "Content gap", "Local SEO", "Conversion"], required: true },
      { key: "gsc_access", label: "Will you grant Google Search Console access?", type: "select", options: ["Yes", "No"], required: true },
      { key: "ga_access", label: "Will you grant Google Analytics access?", type: "select", options: ["Yes", "No"], required: true },
      { key: "competitors", label: "Top 3 competitors", type: "textarea", required: true, full: true, placeholder: "one URL per line" },
      COMMON_NOTES,
    ],
  },

  "blog-content-plan": {
    title: "Blog Content Plan — order brief",
    fields: [
      { key: "website_url", label: "Website URL", type: "url", required: true },
      { key: "niche", label: "Niche / topic area", type: "text", required: true, placeholder: "B2B SaaS, fitness, parenting…" },
      { key: "audience", label: "Target audience", type: "textarea", required: true, full: true, placeholder: "Who is reading this? Pain points, role, seniority…" },
      { key: "monthly_volume", label: "Target posts / month", type: "select", required: true, options: ["4", "8", "12", "16", "20+"] },
      { key: "primary_market", label: "Primary market (country)", type: "select", options: COUNTRY_OPTIONS, required: true },
      { key: "competitors", label: "Competitor blogs we should outrank", type: "textarea", required: true, full: true, placeholder: "one URL per line" },
      { key: "topic_seeds", label: "Seed topics or keywords you already care about (optional)", type: "textarea", full: true, placeholder: "one per line" },
      COMMON_NOTES,
    ],
  },
};

export const FIELD_LABELS = Object.values(SERVICE_FORMS).reduce((acc, f) => {
  f.fields.forEach((field) => { acc[field.key] = field.label; });
  return acc;
}, {});
